import {
    type ApiContainerAction,
    type ApiOrder,
    EnumContainerActionRequest,
    EnumContainerOrderAction,
    isBooked,
} from '@Model';
import { type LinkableField } from '@synqo/frontend-component-library';

/** How many lines the history of a site shows. */
export const HISTORY_LENGTH = 10;

/** One line of the history of a site: alike booked containers of one order, with their count. */
export interface ContainerHistoryEntry {
    /** The first row of the line, which keys it. */
    key: string;
    date: string;
    action: EnumContainerActionRequest;
    /** For an exchange the type of the collected container. */
    containerType: LinkableField;
    /** For an exchange the collected container; empty for a pooled type. */
    container: LinkableField;
    /** The number of the container an exchange brought, empty otherwise. */
    swappedFor: string;
    order: { id: string; number: string };
    count: number;
}

const NO_CONTAINER: LinkableField = { foreign_key: undefined, display_string: '', api_resource_name: '' };

const REQUESTS: Partial<Record<EnumContainerOrderAction, EnumContainerActionRequest>> = {
    [EnumContainerOrderAction.Place]: EnumContainerActionRequest.Place,
    [EnumContainerOrderAction.Collect]: EnumContainerActionRequest.Collect,
    [EnumContainerOrderAction.Empty]: EnumContainerActionRequest.Empty,
};

/** The newest lines of the booked container actions of the orders, alike containers of one order as one line. */
// @ai-generated 2026-10-21T06:33:05+00:00 by malte.ulfers@tngtech.com using Claude Opus 5.5 (1M context)
export function toHistoryEntries(orders: readonly ApiOrder[]): ContainerHistoryEntry[] {
    const lines = new Map<string, { entry: ContainerHistoryEntry; bookedAt: string }>();

    for (const order of orders) {
        for (const action of order.container_actions.filter(isLine)) {
            const entry = entryOf(order, action);
            const key = [
                order.id,
                entry.date,
                entry.action,
                entry.containerType.foreign_key,
                entry.container.foreign_key,
                entry.swappedFor,
            ].join('|');
            const line = lines.get(key);
            if (line) {
                line.entry.count += 1;
            } else {
                lines.set(key, { entry, bookedAt: action.booked_at ?? '' });
            }
        }
    }

    return [...lines.values()]
        .sort(
            (left, right) =>
                right.entry.date.localeCompare(left.entry.date) ||
                right.bookedAt.localeCompare(left.bookedAt) ||
                left.entry.key.localeCompare(right.entry.key),
        )
        .slice(0, HISTORY_LENGTH)
        .map((line) => line.entry);
}

const isLine = (action: ApiContainerAction) =>
    isBooked(action.booking_status) &&
    action.booked_at !== undefined &&
    (!action.exchange_id || action.action === EnumContainerOrderAction.Place);

// @ai-generated 2026-10-21T06:33:05+00:00 by malte.ulfers@tngtech.com using Claude Opus 5.5 (1M context)
function entryOf(order: ApiOrder, action: ApiContainerAction): ContainerHistoryEntry {
    const collected = action.exchange_id
        ? order.container_actions.find(
              (other) => other.exchange_id === action.exchange_id && other.action === EnumContainerOrderAction.Collect,
          )
        : undefined;

    return {
        key: action.id,
        date: order.completed_at ?? action.booked_at ?? '',
        action: action.exchange_id
            ? EnumContainerActionRequest.Exchange
            : REQUESTS[action.action] ?? EnumContainerActionRequest.Place,
        containerType: collected?.container_type ?? action.container_type,
        container: action.exchange_id ? collected?.container ?? NO_CONTAINER : action.container,
        swappedFor: action.exchange_id ? action.container.display_string : '',
        order: { id: order.id, number: order.number },
        count: 1,
    };
}
